import { loadConnectAndInitialize } from "@stripe/connect-js"
import useStripeInfo from "hooks/use_stripe_info"
import { useCallback, useMemo, useState } from "react"
import { resolveCssPropertiesFromObject } from "utils/css"
import useApiAction from "./use_api_action"

export default function useStripeConnectInstance() {
  const { publishableKey } = useStripeInfo()
  const createAccountSessionAction = useApiAction({ path: "/stripe_account_session" })
  const [isTestModeMismatch, setIsTestModeMismatch] = useState(false)

  const fetchClientSecret = useCallback(async () => {
    const clientSecret = await createAccountSessionAction
      .run()
      .then(response => {
        return response.data.clientSecret
      })
      .catch(error => {
        if (
          Object.hasOwn(error, "errors") &&
          error.errors.find(e => e.title === "Test mode mismatch error")
        ) {
          setIsTestModeMismatch(true)
        } else {
          Bugsnag.notifyException(error, "StripeAccountSessionError")
        }
        return undefined
      })
    return clientSecret
  }, [])

  const stripeConnectStyleVariables = useMemo(
    () =>
      resolveCssPropertiesFromObject({
        colorPrimary: "var(--t-fill-color-interaction-default)",
        colorText: "var(--t-text-color-default-primary)",
        buttonSecondaryColorBackground: "var(--t-fill-color-neutral-100)",
        buttonSecondaryColorBorder: "var(--t-fill-color-neutral-050)",
        colorSecondaryText: "var(--t-fill-color-neutral-020)",
        colorBorder: "var(--t-fill-color-neutral-060)",
        // Tapestry status badge colors:
        badgeNeutralColorBackground: "var(--t-fill-color-neutral-100)",
        badgeNeutralColorBorder: "var(--t-fill-color-neutral-030)",
        badgeNeutralColorText: "var(--t-fill-color-neutral-010)",
        badgeSuccessColorBackground: "var(--t-fill-color-neutral-100)",
        badgeSuccessColorBorder: "var(--t-border-color-status-success)",
        badgeSuccessColorText: "var(--t-text-color-status-success)",
        badgeWarningColorBackground: "var(--t-fill-color-neutral-100)",
        badgeWarningColorBorder: "var(--t-border-color-status-warning)",
        badgeWarningColorText: "var(--t-text-color-status-warning)",
        badgeDangerColorBackground: "var(--t-fill-color-neutral-100)",
        badgeDangerColorBorder: "var(--t-border-color-status-error)",
        badgeDangerColorText: "var(--t-text-color-status-error)",
        badgeBorderRadius: "24px", // max allowed (rounded pill)
        fontFamily: "inherit",
        headingLgFontWeight: "500",
        headingMdFontWeight: "500",
        headingSmFontWeight: "500",
        headingXsFontWeight: "500"
      }),
    []
  )

  const stripeConnectInstance = useMemo(
    () =>
      loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret,
        appearance: {
          overlays: "dialog",
          variables: stripeConnectStyleVariables
        }
      }),
    [publishableKey, fetchClientSecret, stripeConnectStyleVariables]
  )

  return { stripeConnectInstance, isTestModeMismatch }
}
